import {
  FC,
  useContext,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
} from "react";
import { useModalAction } from "../../modal-views/context";
import { useMutation } from "react-query";
import client from "../../../api";
import { convertPaymentSystem } from "../../../lib/utils";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  getErrorMessage,
  getStatusStyle,
  makeNumberWithFloatingDigits,
} from "../../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Form, Input, InputNumber, Table } from "antd";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
} from "src/lib/constants";
import { Fee, Limitation, PaginationInput, User } from "src/api/types";
import SearchUserIDBox from "src/components/shared/search-box";
import {
  FeeDataType,
  LimitationDataType,
  UserDataType,
} from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import { useLocation } from "react-router-dom";
import routes from "src/config/routes";
import { LockIcon } from "src/components/icons/lock-icon";
import { UnlockIcon } from "src/components/icons/unlock-icon";
import Button from "src/components/ui/button";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import UsersContainerPage from "src/components/users";
import { EditIcon } from "src/components/icons/edit-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import { CheckIcon } from "src/components/icons/check-icon";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: FeeDataType;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

type UserLimitationsPageProps = {
  id: number;
  user: User;
};

const GeneralUserLimitationsPage: FC<UserLimitationsPageProps> = ({
  id,
  user,
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record: LimitationDataType) => record.key === editingKey;
  const { openModal, closeModal } = useModalAction();

  const [dataSource, setDataSource] = useState<LimitationDataType[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [updatedData, setUpdatedData] = useState<LimitationDataType | null>(
    null
  );

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 908 ? true : false);
    setIsSmall(window.innerWidth < 530 ? true : false);
  }

  const { mutate: getList, isLoading } = useMutation(
    user.is_business
      ? client.users.getBuisnessLimitations
      : client.users.getIndividualLimitations,
    {
      onSuccess: (data) => {
        const sortData = data.sort(
          (a: any, b: any) => a.asset_code - b.asset_code
        );
        setDataSource(
          sortData.map((item) => ({
            key: item.id,
            ...item,
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const editOnMobile = (key: number) => {
    const selData = dataSource.filter((item) => item.key === key)[0];
    openModal("UPDATE_GENERAL_LIMITATIONS_VIEW", {
      ...selData,
      callback: onCallBack,
    });
  };

  const onCallBack = (data: Limitation) => {
    console.log(data);
    setEditingKey(String(data.id));
    setUpdatedData({ key: data.id, ...data });

    setTimeout(() => {
      updateLimitations({ input: data, userId: id });
    }, 100);
  };

  const { mutate: updateLimitations, isLoading: isLoadingUpdate } = useMutation(
    user.is_business
      ? client.users.updateBuisnessLimitation
      : client.users.updateIndividualLimitation,
    {
      onSuccess: (data) => {
        if (updatedData) {
          const newData = [...dataSource];
          const index = newData.findIndex((item) => editingKey == item.key);
          if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
              ...item,
              ...updatedData!,
            });
            setDataSource(newData);
            setEditingKey("");
          } else {
            newData.push(updatedData!);
            setDataSource(newData);
            setEditingKey("");
          }
        } else {
          setEditingKey("");
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  useEffect(() => {
    isMounted && getList(id);
  }, [isMounted, id]);

  const edit = (record: Partial<LimitationDataType> & { key: React.Key }) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key as string);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as LimitationDataType;
      console.log(row);
      setUpdatedData(row);

      setTimeout(() => {
        updateLimitations({ input: { ...row, id: key as number }, userId: id });
      }, 300);
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Currency Name
        </p>
      ),
      dataIndex: "asset_code",
      key: "asset_code",
      className: "w-auto",
      editable: false,
      render: (value: string) => {
        return (
          <div className="flex flex-row gap-2 items-center">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {value?.toUpperCase()}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Channel Name
        </p>
      ),
      dataIndex: "channel_name",
      key: "channel_name",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {convertPaymentSystem(value)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Deposit min
        </p>
      ),
      dataIndex: "deposit_min",
      key: "deposit_min",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Deposit max
        </p>
      ),
      dataIndex: "deposit_max",
      key: "deposit_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Withdraw min
        </p>
      ),
      dataIndex: "withdraw_min",
      key: "withdraw_min",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Withdraw max
        </p>
      ),
      dataIndex: "withdraw_max",
      key: "withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Withdraw min
        </p>
      ),
      dataIndex: "vip_withdraw_min",
      key: "vip_withdraw_min",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Withdraw max
        </p>
      ),
      dataIndex: "vip_withdraw_max",
      key: "vip_withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Deposit min
        </p>
      ),
      dataIndex: "vip_deposit_min",
      key: "vip_deposit_min",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Deposit max
        </p>
      ),
      dataIndex: "vip_deposit_max",
      key: "vip_deposit_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_: any, record: LimitationDataType) => {
        const editable = isEditing(record);
        return editable ? (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={cancel}
            >
              <CloseIcon className="w-[20px] h-[20px] text-secondary" />
            </button>
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => save(record.key)}
            >
              <CheckIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => edit(record)}
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: LimitationDataType) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: "",
        editing: isEditing(record),
      }),
    };
  });

  const mobileColumns: ColumnsType<LimitationDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-4 items-start justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Currency
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData?.asset_code?.toUpperCase()}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Channel Name
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData?.channel_name}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Depos.min
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.deposit_min, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Depos.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.deposit_max, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Withd.min
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.withdraw_min, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  Withd.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
                </p>
              </div>
            </div>
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  VIP Depos.min
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_deposit_min, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  VIP Depos.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_deposit_max, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  VIP Withd.min
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_withdraw_min, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium">
                  VIP Withd.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[12px] font-manrope font-medium invisible">
                  Edit
                </p>
                <a
                  className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium"
                  onClick={() => editOnMobile(record.key as number)}
                >
                  Edit
                </a>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const smallColumns: ColumnsType<LimitationDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Currency
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {selData?.code?.toUpperCase()}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Depos.min
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.deposit_min, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Depos.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.deposit_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Withd.min
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.withdraw_min, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Withd.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Depos.min
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_deposit_min, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Depos.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_deposit_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Withd.min
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_withdraw_min, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Withd.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium invisible">
                Edit
              </p>
              <a
                className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium"
                onClick={() => editOnMobile(record.key as number)}
              >
                Edit
              </a>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white rounded-r-[12px] rounded-bl-[12px] w-full relative">
      <div className="p-4 relative">
        {isMobile ? (
          <Table
            loading={isLoading || isLoadingUpdate}
            showSorterTooltip={false}
            columns={isSmall ? smallColumns : mobileColumns}
            dataSource={dataSource}
            pagination={false}
          />
        ) : (
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              loading={isLoading || isLoadingUpdate}
              showSorterTooltip={false}
              columns={mergedColumns}
              dataSource={dataSource}
              pagination={false}
              rowClassName="editable-row"
            />
          </Form>
        )}
      </div>
    </div>
  );
};

export default GeneralUserLimitationsPage;
